import { useState } from "react";
import { useProjectContext } from "@contexts/projects";
import { formatNumberToLocale } from "@utils/formatNumber";
import CostOriginTitlesOffcanvas from "./CostOriginTitlesOffcanvas";
import { isPresentationProject } from "@utils/presentationMode";

// Indicador "origem do custo" (Sienge): divide o custo financeiro da obra (pago +
// a pagar) entre o que nasceu no módulo de suprimentos (títulos de pedido de compra
// e contrato) e o que foi lançado direto no contas a pagar. Não inclui os valores
// não lançados da Ampli. Lê activeProject.costOriginSplit (montado no getDetail).

const SUPPLIES_COLOR = "#1D9E75"; // teal
const FINANCE_COLOR = "#EF9F27"; // amber
const DIVIDER = "1px solid rgba(0, 0, 0, 0.08)";

const formatPct = (value) =>
  (Number.isFinite(Number(value)) ? Number(value) : 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const LegendRow = ({ color, label, value, pct, onClick }) => {
  const clickable = typeof onClick === "function";
  return (
    <div
      className="flex justify-content-space-between"
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      aria-label={clickable ? `Ver títulos ${label}` : undefined}
      style={{ alignItems: "center", padding: "8px 0", borderTop: DIVIDER, cursor: clickable ? "pointer" : "default" }}
    >
      <span className="flex" style={{ alignItems: "center", gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block", flexShrink: 0 }} />
        <span>{label}</span>
      </span>
      <span style={{ whiteSpace: "nowrap" }}>
        <span style={{ opacity: 0.6 }}>R$ {formatNumberToLocale(value)}</span>
        &nbsp;&nbsp;
        <strong>{formatPct(pct)}%</strong>
        {clickable && (
          <span aria-hidden style={{ marginLeft: 8, opacity: 0.5 }}>
            ›
          </span>
        )}
      </span>
    </div>
  );
};

const CostOriginSplitCard = () => {
  const { activeProject } = useProjectContext();
  const presentationReadOnly = isPresentationProject(activeProject);
  const [openBucket, setOpenBucket] = useState(null);
  const split = activeProject?.costOriginSplit;
  const projectId = activeProject?._id;

  // API ainda não deployada com o campo → não renderiza nada (degradação suave).
  if (!split) return null;

  const supplies = split.supplies ?? { value: 0, pct: 0 };
  const finance = split.finance ?? { value: 0, pct: 0 };
  const paid = split.paid ?? { supplies: 0, finance: 0 };
  const toPay = split.toPay ?? { supplies: 0, finance: 0 };
  const { coverage = 0, noData = false } = split;
  const total = supplies.value + finance.value;

  const tooltip =
    "Origem do custo da obra no Sienge, sobre pago + a pagar: via suprimentos são os " +
    "títulos que nasceram de pedido de compra ou contrato (rastreáveis por fornecedor, " +
    "material e preço); via financeiro são os lançados direto no contas a pagar " +
    "(impostos, tarifas, financiamento, lançamentos manuais no ERP). " +
    `Pago: R$ ${formatNumberToLocale(paid.supplies)} suprimentos / R$ ${formatNumberToLocale(paid.finance)} financeiro. ` +
    `A pagar: R$ ${formatNumberToLocale(toPay.supplies)} suprimentos / R$ ${formatNumberToLocale(toPay.finance)} financeiro. ` +
    "Não inclui os estouros / economias registrados na plataforma.";

  return (
    <>
      <div className="card" style={{ maxWidth: 520, marginTop: 24, marginBottom: 24 }}>
        <div className="flex justify-content-space-between">
          <span className="card-title">Origem do custo</span>
          <span
            title={tooltip}
            aria-label={tooltip}
            style={{
              cursor: "help",
              fontSize: 12,
              width: 16,
              height: 16,
              lineHeight: "16px",
              textAlign: "center",
              borderRadius: "50%",
              border: "1px solid rgba(0,0,0,0.25)",
              color: "rgba(0,0,0,0.5)",
              flexShrink: 0,
            }}
          >
            i
          </span>
        </div>

        {noData ? (
          <span className="card-label">Sem dados de origem para esta obra.</span>
        ) : (
          <>
            <div style={{ display: "flex", height: 12, borderRadius: 999, overflow: "hidden", margin: "14px 0 8px" }}>
              <div style={{ width: `${supplies.pct}%`, background: SUPPLIES_COLOR }} />
              <div
                style={{
                  width: `${finance.pct}%`,
                  minWidth: finance.value > 0 ? 5 : 0,
                  background: FINANCE_COLOR,
                }}
              />
            </div>

            <LegendRow
              color={SUPPLIES_COLOR}
              label="Via suprimentos"
              value={supplies.value}
              pct={supplies.pct}
              onClick={supplies.value > 0 ? () => setOpenBucket("supplies") : undefined}
            />
            <LegendRow
              color={FINANCE_COLOR}
              label="Via financeiro"
              value={finance.value}
              pct={finance.pct}
              onClick={finance.value > 0 ? () => setOpenBucket("finance") : undefined}
            />

            <div
              className="flex justify-content-space-between"
              style={{ alignItems: "center", paddingTop: 12, marginTop: 4, borderTop: DIVIDER }}
            >
              <span className="card-label">Pago + a pagar</span>
              <strong>R$ {formatNumberToLocale(total)}</strong>
            </div>

            {coverage < 0.95 && (
              <span className="card-label" style={{ display: "block", marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                Origem conhecida para {formatPct(coverage * 100)}% do valor — o restante ainda
                não foi reprocessado com a origem do título.
              </span>
            )}
          </>
        )}
      </div>

      <CostOriginTitlesOffcanvas
        open={!!openBucket}
        bucket={openBucket}
        projectId={projectId}
        header={openBucket === "supplies" ? supplies : finance}
        readOnly={presentationReadOnly}
        onClose={() => setOpenBucket(null)}
      />
    </>
  );
};

export default CostOriginSplitCard;
